import { GraduationCap, Briefcase, Sparkles } from 'lucide-react';
import { TID } from '@/constants/testIds';
import { FOUNDERS } from '@/constants/content';

const TID_MAP = {
    deepti: TID.founderDeepti,
    bhartendu: TID.founderBhartendu,
    shib: TID.founderShib,
};

export default function Founders() {
    return (
        <section id="founders" className="section-pad bg-soft">
            <div className="container-wl">
                <div className="max-w-3xl">
                    <div className="flex items-center gap-3 mb-5">
                        <span className="gold-rule" />
                        <span className="overline">Meet The Founders</span>
                    </div>
                    <h2 className="font-display-bold text-ink text-4xl lg:text-[52px] leading-[1.05] tracking-tight">
                        Three practitioners. <span className="italic text-gold-600">One client-first firm.</span>
                    </h2>
                    <p className="mt-5 text-ink/70 text-[16.5px] leading-relaxed">
                        Together, our founders bring 62+ years of combined experience guiding families,
                        professionals and business owners through every stage of their financial lives.
                    </p>
                </div>

                <div className="mt-14 grid md:grid-cols-3 gap-6 lg:gap-8">
                    {FOUNDERS.map((f) => (
                        <article
                            key={f.id}
                            data-testid={TID_MAP[f.id]}
                            className="group bg-white border border-hairline overflow-hidden hover-lift rounded-xl"
                        >
                            <div className="relative aspect-[4/5] overflow-hidden bg-soft">
                                <img
                                    src={f.photo}
                                    alt={f.name}
                                    className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
                                    loading="lazy"
                                />
                                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-navy/70 to-transparent" />
                                <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-white/90 backdrop-blur-md px-3 py-1.5">
                                    <Sparkles className="w-3.5 h-3.5 text-gold-600" />
                                    <span className="text-[11px] uppercase tracking-widest2 text-ink">
                                        {f.years} Yrs Experience
                                    </span>
                                </div>
                            </div>

                            <div className="p-7">
                                <p className="overline">{f.role}</p>
                                <h3 className="font-display-bold text-ink text-[26px] leading-tight mt-1">
                                    {f.name}
                                </h3>

                                <div className="mt-5 space-y-3 text-[13.5px] text-ink/80">
                                    {f.qualifications.map((q) => (
                                        <div key={q} className="flex items-start gap-2.5">
                                            <GraduationCap className="w-4 h-4 text-gold-600 shrink-0 mt-0.5" />
                                            <span>{q}</span>
                                        </div>
                                    ))}
                                </div>

                                <p className="mt-5 text-[14.5px] text-ink/70 leading-relaxed">
                                    {f.bio}
                                </p>

                                <div className="mt-6 pt-5 border-t border-hairline">
                                    <p className="text-[11px] uppercase tracking-widest2 text-ink/50 mb-3">
                                        Areas of Expertise
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {f.expertise.map((e) => (
                                            <span
                                                key={e}
                                                className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 bg-soft border border-hairline text-ink/80"
                                            >
                                                <Briefcase className="w-3 h-3 text-forest" />
                                                {e}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>

                {/* Leadership Highlight */}
                <div className="mt-16 lg:mt-20 relative overflow-hidden rounded-xl bg-navy text-white p-10 lg:p-16 text-center">
                    <div className="absolute inset-0 grain opacity-30" />
                    <p className="overline relative">Leadership at a Glance</p>
                    <p className="relative mt-4 font-display-bold text-[56px] lg:text-[88px] leading-none text-gold">
                        62+
                    </p>
                    <p className="relative mt-3 text-[15px] tracking-wide text-white/80 max-w-xl mx-auto">
                        Years of combined professional experience guiding families, business owners and
                        professionals across every stage of their financial journey.
                    </p>
                </div>
            </div>
        </section>
    );
}
